import { api } from './api-client';
import { cached, invalidatePrefix } from './api-cache';
import type {
  NormalizedTicketFieldOption,
  TicketFieldDefinition,
  TicketFieldDependency,
  TicketFieldOptionMapping,
} from './ticket-field-registry';

export type TicketFieldRegistryField = TicketFieldDefinition & {
  dependsOnFieldKey?: string | null;
};

export async function getTicketFields(): Promise<TicketFieldRegistryField[]> {
  return cached('ticket-fields', async () => {
    const response = await api.get<{ data: TicketFieldRegistryField[] }>('/ticket-fields');
    return response.data;
  });
}

export async function getTicketField(fieldKey: string): Promise<TicketFieldRegistryField> {
  const response = await api.get<{ data: TicketFieldRegistryField }>(
    `/ticket-fields/${encodeURIComponent(fieldKey)}`,
  );
  return response.data;
}

export async function getTicketFieldOptions(
  fieldKey: string,
  parentValue?: string,
): Promise<NormalizedTicketFieldOption[]> {
  const key = parentValue
    ? `ticket-field-options:${fieldKey}:${parentValue}`
    : `ticket-field-options:${fieldKey}`;
  return cached(key, async () => {
    const response = await api.get<{ data: NormalizedTicketFieldOption[] }>(
      `/ticket-fields/${encodeURIComponent(fieldKey)}/options`,
      { params: { parentValue } },
    );
    return response.data;
  });
}

export async function getTicketFieldDependencies(fieldKey: string): Promise<TicketFieldDependency[]> {
  const response = await api.get<{ data: TicketFieldDependency[] }>(
    `/ticket-fields/${encodeURIComponent(fieldKey)}/dependencies`,
  );
  return response.data;
}

export type TicketFieldUpdate = Partial<Pick<
  TicketFieldRegistryField,
  | 'label'
  | 'description'
  | 'labelForCustomers'
  | 'requiredForAgents'
  | 'requiredForCustomers'
  | 'requiredForClosure'
  | 'displayedToCustomers'
  | 'customersCanEdit'
  | 'customersCanFilter'
  | 'isActive'
  | 'optionSourceType'
  | 'optionSourceId'
  | 'dependsOnFieldId'
  | 'dependsOnFieldKey'
>> & { choices?: string[] };

export interface CreateTicketFieldInput extends TicketFieldUpdate {
  fieldKey: string;
  rendererType: TicketFieldRegistryField['rendererType'];
  choices?: string[];
}

export async function createTicketField(input: CreateTicketFieldInput): Promise<TicketFieldRegistryField> {
  const response = await api.post<{ data: TicketFieldRegistryField }>('/ticket-fields', input);
  invalidatePrefix('ticket-field');
  return response.data;
}

export async function updateTicketField(
  fieldKey: string,
  input: TicketFieldUpdate,
): Promise<TicketFieldRegistryField> {
  const response = await api.put<{ data: TicketFieldRegistryField }>(
    `/ticket-fields/${encodeURIComponent(fieldKey)}`,
    input,
  );
  invalidatePrefix('ticket-field');
  return response.data;
}

export async function reorderTicketFields(fieldKeys: string[]): Promise<TicketFieldRegistryField[]> {
  const response = await api.put<{ data: TicketFieldRegistryField[] }>('/ticket-fields/order', { fieldKeys });
  invalidatePrefix('ticket-field');
  return response.data;
}

export async function addTicketFieldOption(
  fieldKey: string,
  value: string,
  label?: string,
): Promise<TicketFieldRegistryField> {
  const response = await api.post<{ data: TicketFieldRegistryField }>(
    `/ticket-fields/${encodeURIComponent(fieldKey)}/options`,
    { value, label },
  );
  invalidatePrefix('ticket-field');
  return response.data;
}

export async function updateTicketFieldOption(
  fieldKey: string,
  optionValue: string,
  label: string,
): Promise<TicketFieldRegistryField> {
  const response = await api.put<{ data: TicketFieldRegistryField }>(
    `/ticket-fields/${encodeURIComponent(fieldKey)}/options/${encodeURIComponent(optionValue)}`,
    { label },
  );
  invalidatePrefix('ticket-field');
  return response.data;
}

export async function archiveTicketFieldOption(
  fieldKey: string,
  optionValue: string,
): Promise<TicketFieldRegistryField> {
  const response = await api.delete<{ data: TicketFieldRegistryField }>(
    `/ticket-fields/${encodeURIComponent(fieldKey)}/options/${encodeURIComponent(optionValue)}`,
  );
  invalidatePrefix('ticket-field');
  return response.data;
}

export async function getDependencyChildOptions(
  dependencyId: string,
  parentOptionId: string,
): Promise<{ id: string; value: string; label: string; active: boolean; position: number }[]> {
  const response = await api.get<{ data: { id: string; value: string; label: string; active: boolean; position: number }[] }>(
    `/admin/field-dependencies/${dependencyId}/options`,
    { params: { parentOptionId } },
  );
  return response.data;
}

export async function getDependencyMappings(dependencyId: string): Promise<TicketFieldOptionMapping[]> {
  const response = await api.get<{ data: TicketFieldOptionMapping[] }>(
    `/admin/field-dependencies/${dependencyId}/mappings`,
  );
  return response.data;
}

export async function setDependencyMappings(
  dependencyId: string,
  parentOptionId: string,
  childOptionIds: string[],
): Promise<void> {
  await api.post(`/admin/field-dependencies/${dependencyId}/options`, {
    parentOptionId,
    childOptionIds,
  });
}
